import { Link } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { formatUSD } from "@/lib/api";
import { toast } from "sonner";

const ROWS = [
  { key: "type", label: "Device type" },
  { key: "coverage", label: "Coverage" },
  { key: "battery", label: "Battery" },
  { key: "fall_detection", label: "Fall detection" },
  { key: "gps", label: "GPS location" },
  { key: "water", label: "Water resistance" },
];

export const ComparisonTable = ({ highlight }) => {
  const { data: products = [] } = useProducts();
  const { addItem } = useCart();
  if (!products.length) return null;

  const handleAdd = (p) => {
    addItem(p);
    toast.success(`${p.name} added to cart`, { description: "Free US shipping · No long-term contract" });
  };

  return (
    <div className="overflow-x-auto rounded-3xl border border-brand-line bg-white shadow-card" data-testid="comparison-table">
      <table className="w-full min-w-[840px] text-sm">
        <thead>
          <tr>
            <th className="w-44 p-4 text-left align-bottom">
              <span className="eyebrow text-brand-red">
                <span className="inline-block h-px w-7 bg-brand-red" />
                Compare
              </span>
            </th>
            {products.map((p) => (
              <th key={p.slug} className={`relative p-4 pt-6 text-center align-bottom ${highlight === p.slug ? "bg-brand-navy/5 border-t-4 border-brand-red" : ""}`}>
                {highlight === p.slug && (
                  <span className="absolute top-1.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide text-brand-red">This device</span>
                )}
                <Link to={`/products/${p.slug}`} data-testid={`compare-link-${p.slug}`} className="btn-focus group inline-block">
                  <img src={p.image} alt={p.name} loading="lazy" className="h-24 w-24 object-contain mx-auto rounded-xl" />
                  <span className="block mt-2 font-display font-bold text-base text-brand-navy group-hover:text-brand-red transition-colors">
                    {p.name}
                  </span>
                </Link>
                <span className="block mt-1 font-display font-extrabold text-xl text-brand-navy">{formatUSD(p.price)}</span>
                <button
                  onClick={() => handleAdd(p)}
                  data-testid={`compare-add-${p.slug}`}
                  className="btn-focus mt-3 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.8} /> Add
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.key} className="border-t border-brand-line">
              <td className="p-4 font-bold text-brand-navy">{row.label}</td>
              {products.map((p) => {
                const v = p.compare?.[row.key] || "—";
                const auto = row.key === "fall_detection" && v === "Automatic";
                return (
                  <td key={p.slug} className={`p-4 text-center text-brand-ink2 ${highlight === p.slug ? "bg-brand-navy/5" : ""}`}>
                    {auto ? (
                      <span className="inline-flex items-center gap-1.5 font-bold text-brand-red">
                        <Check className="w-4 h-4" strokeWidth={3} /> Automatic
                      </span>
                    ) : (
                      v
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
